import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '../services/notification-service/notification-service';
import { ErrorReportingService } from '../services/error-reporting-service/error-reporting-service';
import { isLicenseRefusal } from '../models/problem-detail.model';
import { ApiErrorDescription, ApiErrorMessages, describeApiError } from './api-error';

/**
 * The one way a failed request reaches the user: the API's own `detail` (or the generic sentence
 * when it explained nothing), a Sentry report only for a failure the API never answered — a coded
 * response is already captured by the backend — and nothing at all for a license refusal the
 * interceptor has already explained. A call site with its own recovery — a locked
 * ŠVP, a page state — decides that first and comes here for the rest.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiErrorHandler {
  private readonly notification = inject(NotificationService);
  private readonly errorReporting = inject(ErrorReportingService);

  /**
   * What to tell the user, with an unanswered failure already reported. For a caller that renders the message
   * itself — inline in a dialog, in a chat panel.
   *
   * @param context Sentry context, e.g. `'SubjectService.deleteSubject'`
   * @param overrides Czech messages for the codes this caller can phrase better than the API
   */
  describe(error: unknown, context: string, overrides?: ApiErrorMessages): ApiErrorDescription {
    const description = describeApiError(error, overrides);
    if (description.report) {
      this.errorReporting.report(error, context);
    }
    return description;
  }

  /** {@link describe}, shown as a persistent snack bar — unless the license interceptor already spoke. */
  notify(error: unknown, context: string, overrides?: ApiErrorMessages): void {
    if (error instanceof HttpErrorResponse && isLicenseRefusal(error)) {
      return;
    }
    this.notification.showPersistentError(
      this.describe(error, context, overrides).message,
      'Zavřít',
    );
  }
}
